import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import stitchAxios from '../utils/axios'
import axiosLib from 'axios'

export interface GiftCardProduct {
  id: number
  numberOfMonths: number
  quantityText: string
  amount: number
  grandTotal: number
  status: string
  purchasedCount: number
}

interface GiftCardStore {
  products: Record<string, GiftCardProduct[]>
  supportedMonthOptions: Record<string, number[]>
  fetchGiftCardProducts: (membershipId: string) => Promise<void>
  createGiftCardProduct: (membershipId: string, numberOfMonths: number) => Promise<void>
  updateGiftCardProduct: (membershipId: string, id: number, status: string) => Promise<void>
  removeGiftCardProduct: (membershipId: string, id: number) => Promise<GiftCardProduct | void>
}

const createFunction = (set: Function, get: Function): GiftCardStore => ({
  products: {},
  supportedMonthOptions: {},

  fetchGiftCardProducts: async (membershipId: string) => {
    const response = await stitchAxios.get(`/memberships/${membershipId}/gift_card_products`)
    set((state: GiftCardStore) => ({
      ...state,
      products: { ...state.products, [membershipId]: response.data.giftCardProducts },
      supportedMonthOptions: { ...state.supportedMonthOptions, [membershipId]: response.data.supportedMonthOptions },
    }))
  },

  createGiftCardProduct: async (membershipId: string, numberOfMonths: number) => {
    try {
      const response = await stitchAxios.post(`/memberships/${membershipId}/gift_card_products`, {
        number_of_months: numberOfMonths,
      })
      const newProduct: GiftCardProduct = response.data
      set((state: GiftCardStore) => ({
        ...state,
        products: {
          ...state.products,
          [membershipId]: [...(state.products[membershipId] || []), newProduct],
        },
      }))
    } catch (e) {
      if (axiosLib.isAxiosError(e)) {
        throw new Error((e.response && e.response.data.message) || 'Could not create gift card option.')
      }
      throw e
    }
  },

  updateGiftCardProduct: async (membershipId: string, id: number, status: string) => {
    try {
      const response = await stitchAxios.patch(
        `/memberships/${membershipId}/gift_card_products/${id}`,
        { status }
      )
      const updated: GiftCardProduct = response.data
      set((state: GiftCardStore) => ({
        ...state,
        products: {
          ...state.products,
          [membershipId]: (state.products[membershipId] || []).map((p) =>
            p.id === id ? updated : p
          ),
        },
      }))
    } catch (e) {
      if (axiosLib.isAxiosError(e)) {
        throw new Error((e.response && e.response.data.message) || 'Could not update gift card option.')
      }
      throw e
    }
  },

  removeGiftCardProduct: async (membershipId: string, id: number): Promise<GiftCardProduct | void> => {
    try {
      const response = await stitchAxios.delete(
        `/memberships/${membershipId}/gift_card_products/${id}`
      )
      // 204 No Content means truly deleted; 200 means deactivated
      if (response.status === 204) {
        set((state: GiftCardStore) => ({
          ...state,
          products: {
            ...state.products,
            [membershipId]: (state.products[membershipId] || []).filter((p) => p.id !== id),
          },
        }))
      } else {
        // Refresh list to reflect deactivated status
        const listResponse = await stitchAxios.get(`/memberships/${membershipId}/gift_card_products`)
        set((state: GiftCardStore) => ({
          ...state,
          products: { ...state.products, [membershipId]: listResponse.data.giftCardProducts },
        }))
      }
    } catch (e) {
      if (axiosLib.isAxiosError(e)) {
        throw new Error((e.response && e.response.data.message) || 'Could not remove gift card option.')
      }
      throw e
    }
  },
})

export const useGiftCardsStore = create<GiftCardStore>(devtools(createFunction) as any)

export default useGiftCardsStore
